import {memo, useMemo} from 'react';

import {pick} from 'lodash';
import moment from 'moment-timezone';

import {getLocalizationService} from '~/shared/services/localisationService';
import {getArrivalText} from '~/shared/utils/dates';
import {getTimeRangeLabel, isDuplicatedTimeRange, isValidTimeRangeString} from '~/shared/utils/timeRangeHelper';
import {ActiveOrder, Order} from '~/shared/services/apiService/models';
import {ComponentBySize} from '~/shared/components/UserTransactions/shared';
import {Root} from '~/shared/components/UserTransactions/components/styled';

import DesktopActiveTable from './DesktopActiveTable';
import MobileActiveOrders from './MobileActiveTable';

type ActiveOrdersProps = {
  activeOrders: ActiveOrder[];
  orderList: Order[];
  tableHeaderCustomColor?: string;
};

const ActiveOrders = ({activeOrders, orderList, tableHeaderCustomColor}: ActiveOrdersProps) => {
  const {t} = getLocalizationService();

  const columns = useMemo(
    () => [
      {
        name: 'collapse',
        hideLabel: true,
        width: 32,
        height: 32,
      },
      {
        name: 'dateAndTime',
        label: t('date_and_time'),
        width: 150,
      },
      {
        name: 'resName',
        label: t('business_name_2'),
        width: 260,
        grow: true,
      },
      {
        name: 'etaDelivery',
        label: t('eta_delivery'),
        width: 150,
      },
      {
        name: 'orderStatus',
        label: t('order_status'),
        width: 105,
      },
      {
        name: 'orderPrice',
        label: t('total'),
        width: 80,
        grow: true,
      },
      {
        name: 'menu',
        grow: true,
        width: 30,
      },
    ],
    [t],
  );

  const getFormattedDate = (date: string, range?: string): string | undefined => {
    return isValidTimeRangeString(range) && !isDuplicatedTimeRange(range) ?
      getTimeRangeLabel(range) :
      moment(date).tz('Israel').format('DD.MM.YY HH:mm');
  };
 
  const modifiedActiveOrders = useMemo(() => {
    if (!activeOrders) {
      return [];
    }

    return activeOrders?.map((activeOrder: ActiveOrder) => {
      const foundFullOrder = orderList.find(item => item.orderId === activeOrder.orderId);

      let orderStatus = t(activeOrder.orderStatus);

      if (
        activeOrder.orderStatus.toLowerCase() === 'pending' ||
        activeOrder.orderStatus.toLowerCase() === 'inprocess'
      ) {
        if (activeOrder.isFutureOrder) {
          orderStatus = t('future_order');
        }
        if (activeOrder.isPoolOrder) {
          orderStatus = t('pooled_order');
        }
      }

      const pooledArrivalText = `${getArrivalText(activeOrder.orderFutureTime)}, ${activeOrder.orderArrivalTime}`;

      return {
        ...foundFullOrder,
        ...pick(activeOrder, [
          'orderId',
          'orderPrice',
          'isFutureOrder',
          'deliveryMethod',
          'cancellationEnable',
          'resName',
          'resId',
          'orderFutureTime',
          'orderTime',
        ]),
        id: activeOrder.orderId,
        dateAndTime: getFormattedDate(activeOrder.orderTime),
        etaDelivery: activeOrder.isPoolOrder ? pooledArrivalText : getFormattedDate(activeOrder.orderFutureTime, activeOrder.orderArrivalTimeRange),
        orderStatus,
        shouldShowOrderMenuButton: true,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOrders]);

  if (!modifiedActiveOrders || !activeOrders || activeOrders.length === 0 || modifiedActiveOrders.length === 0) {
    return null;
  }
  
  return (
    <Root>
      <ComponentBySize
        MobileComponent={MobileActiveOrders}
        DesktopComponent={DesktopActiveTable}
        props={{columns, rows: modifiedActiveOrders, tableHeaderCustomColor}}
      />
    </Root>
  );
};

export default memo(ActiveOrders);
